import { useRef, useState } from 'react'
import { track } from '@vercel/analytics'
import emailjs from 'emailjs-com'

const FIELD_ORDER = ['user_name', 'user_email', 'user_subject', 'message']

const ContactForm = () => {
  const fieldRefs = useRef({})
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_subject: '',
    message: ''
  })

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  })

  const [errors, setErrors] = useState({})

  const validateForm = () => {
    const newErrors = {}

    if (!formData.user_name.trim()) {
      newErrors.user_name = 'Name is required'
    }

    if (!formData.user_email.trim()) {
      newErrors.user_email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.user_email)) {
      newErrors.user_email = 'Please enter a valid email address'
    }

    if (!formData.user_subject.trim()) {
      newErrors.user_subject = 'Subject is required'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long'
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      const firstInvalid = FIELD_ORDER.find((field) => newErrors[field])
      if (firstInvalid) {
        setTimeout(() => fieldRefs.current[firstInvalid]?.focus(), 0)
      }
      return false
    }

    return true
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setFormStatus({
      isSubmitting: true,
      isSubmitted: false,
      error: null
    })

    try {
      const templateParams = {
        from_name: formData.user_name,
        user_email: formData.user_email,
        user_subject: formData.user_subject,
        message: formData.message,
        to_name: 'Griffins Ngeno'
      }

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_bjpd4wb',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_k5qiwza',
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID || '4lXhxvpqKvobtX52F'
      )

      // Analytics: track successful contact form submission
      try {
        track('contact_form_submitted', {
          subject: formData.user_subject,
          emailDomain: formData.user_email.split('@')[1] || 'unknown'
        })
      } catch (_) {}

      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        error: null
      })

      // Reset form
      setFormData({
        user_name: '',
        user_email: '',
        user_subject: '',
        message: ''
      })

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({
          ...prev,
          isSubmitted: false
        }))
      }, 5000)

    } catch (error) {
      console.error('Email send error:', error)
      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        error: 'Failed to send message. Please try again or contact me directly via email.'
      })
    }
  }

  const inputClasses = "w-full min-h-[48px] px-4 py-3 border border-white/[0.08] rounded-xl focus:ring-2 focus:ring-ant-gold/40 focus:border-ant-gold/30 transition-all duration-200 bg-ant-bg-2 text-white text-base placeholder:text-white/25"
  const errorClasses = "text-red-400 text-sm mt-1 flex items-center"
  const labelClasses = "block text-sm font-medium text-white/60 mb-2"

  return (
    <div className="ant-card ant-ring rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-8 lg:p-12" id="contact-form">
      {/* Success Message */}
      {formStatus.isSubmitted && (
        <div className="mb-8 p-6 bg-ant-green/10 border border-ant-green/20 rounded-2xl" role="status" aria-live="polite">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-ant-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-ant-green">Message delivered</h3>
              <p className="text-white/60 mt-1">
                Thank you for your inquiry. I will respond within two to four hours during business hours.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {formStatus.error && (
        <div className="mb-8 p-6 bg-red-500/10 border border-red-500/20 rounded-2xl" role="alert" aria-live="assertive">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-400">Delivery failed</h3>
              <p className="text-white/60 mt-1">{formStatus.error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name and Email Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="user_name" className={labelClasses}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="user_name"
              name="user_name"
              ref={(el) => { fieldRefs.current.user_name = el }}
              value={formData.user_name}
              onChange={handleChange}
              autoComplete="name"
              aria-invalid={Boolean(errors.user_name)}
              aria-describedby={errors.user_name ? 'user_name-error' : undefined}
              className={`${inputClasses} ${errors.user_name ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="Your full name"
              required
            />
            {errors.user_name && (
              <p id="user_name-error" className={errorClasses} role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.user_name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="user_email" className={labelClasses}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="user_email"
              name="user_email"
              ref={(el) => { fieldRefs.current.user_email = el }}
              value={formData.user_email}
              onChange={handleChange}
              autoComplete="email"
              aria-invalid={Boolean(errors.user_email)}
              aria-describedby={errors.user_email ? 'user_email-error' : undefined}
              className={`${inputClasses} ${errors.user_email ? 'border-red-300 focus:ring-red-500' : ''}`}
              placeholder="you@company.com"
              required
            />
            {errors.user_email && (
              <p id="user_email-error" className={errorClasses} role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.user_email}
              </p>
            )}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="user_subject" className={labelClasses}>
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="user_subject"
            name="user_subject"
            ref={(el) => { fieldRefs.current.user_subject = el }}
            value={formData.user_subject}
            onChange={handleChange}
            aria-invalid={Boolean(errors.user_subject)}
            aria-describedby={errors.user_subject ? 'user_subject-error' : undefined}
            className={`${inputClasses} ${errors.user_subject ? 'border-red-300 focus:ring-red-500' : ''}`}
            placeholder="Project scope, timeline, or inquiry"
            required
          />
          {errors.user_subject && (
            <p id="user_subject-error" className={errorClasses} role="alert">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.user_subject}
            </p>
          )}
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className={labelClasses}>
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            ref={(el) => { fieldRefs.current.message = el }}
            value={formData.message}
            onChange={handleChange}
            rows="6"
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? 'message-error' : undefined}
            className={`${inputClasses} resize-none ${errors.message ? 'border-red-300 focus:ring-red-500' : ''}`}
            placeholder="Describe your product, technical requirements, and expected timeline..."
            required
          />
          {errors.message && (
            <p id="message-error" className={errorClasses} role="alert">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.message}
            </p>
          )}
          <p className="text-sm text-white/30 mt-2">
            Minimum 10 characters. Detailed context helps me respond with a precise scope and timeline.
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={formStatus.isSubmitting}
            className={`w-full flex items-center justify-center min-h-[48px] px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 cursor-pointer ${
              formStatus.isSubmitting
                ? 'bg-white/20 text-white/60 cursor-not-allowed'
                : 'bg-white text-ant-bg-2 hover:bg-white/90 hover:shadow-xl'
            }`}
          >
            {formStatus.isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                Send inquiry
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Additional Info */}
        <div className="text-center pt-6 border-t border-white/[0.06]">
          <p className="text-xs text-white/30 mt-2">
            Typical response within two to four hours — Monday through Friday, 09:00–18:00 GST
          </p>
        </div>
      </form>
    </div>
  )
}

export default ContactForm